import { Modal } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendSingleRequest } from '../../../apis';
import { IMusselLineResource } from '../../../entities/farms.entities';
import { showFeedback } from '../../../store/ui/ui.actions';
import {
  numToStr,
  parseImageUrlToFileItem,
  getUnitOfSpecie,
} from '../../../lib/common.helpers';
import { toMillisecond, toSecond } from '../../../lib/common.helpers';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Input,
  Paragrapgh,
  Subtitle,
} from '../../shared';
import ModalFeedbackView from '../../shared/feedback/ModalFeedbackView';
import TimeTrackInput, {
  toSecTimeTracks,
} from '../../shared/input/TimeTrackInput';
import { ICustomValue } from '../../../entities/automation.entities';
import { loadTags } from '../../../store/automation/automation.actions';
import {
  loadFarmsData,
  loadQueueData,
} from '../../../store/farms/farms.actions';
import { IFileWall, ITimeRange } from '../../../entities/general.entities';
import { selectLang } from '../../../store/ui/ui.selector';
import { getSizeMeasureLabel, translate } from '../../../lib/lang.helper';
import {
  SelectAssessFields,
  SelectFarmsByFarmID,
} from '../../../store/extra/extra.selector';
import { getLineGrowingData } from '../../../lib/farm.helpers';
import BatchAssessView from './BatchAssessView';
import DirectionForm from '../../farm-util/DirectionForm';
import { roundNumber } from '../../../entities/util-functions';
import CustomValuesForm from '../../farm-util/CustomValuesForm';
import MinMaxInputForm from '../SeedingModal/MinMaxInputForm';
import FilesWall from '../../shared/pictures-wall/FilesWall';
import {
  selectProfile,
  selectAccount,
} from '../../../store/auth/auth.selector';
import SelectBoat from '../../farmform/SelectBoat';
import { TaggingManageForm } from '../../lines/LineTagButton';

interface IFormData {
  size_min: number | null;
  size_max: number | null;
  size_avg: number | null;
  min_width?: number | null;
  max_width?: number | null;
  avg_width?: number | null;
  btm_size_min?: number | null;
  btm_size_max?: number | null;
  btm_size_avg?: number | null;
  density?: number | null;
  density_max?: number | null;
  assessment_date: number;
  planned_date_harvest: number;
  ready_to_harvest: boolean;
  estimated_amount?: number | null;
  comment: string | null;
  batch_seed_id?: number | null;
  origin_spat_id?: number | null;
  direction: string | null;
  time_tracks?: ITimeRange[];
  image_pdf_files?: IFileWall[];
  custom_values?: ICustomValue[];
  seeding_id?: number;
  boat_id?: number | null;

  tagged_ids?: number[] | null;
}

const defaultForm: IFormData = {
  size_min: null,
  size_max: null,
  size_avg: null,
  min_width: null,
  max_width: null,
  avg_width: null,
  btm_size_min: null,
  btm_size_max: null,
  btm_size_avg: null,
  planned_date_harvest: moment().toDate().getTime(),
  assessment_date: moment().toDate().getTime(),
  comment: null,
  ready_to_harvest: false,
  direction: null,
  time_tracks: [],
  image_pdf_files: [],
};

interface Props {
  visible: boolean;
  title?: string;
  onCancel: () => void;
  onConfirm: (data: any) => void;
  data?: any;
  lineData: IMusselLineResource;
  updateID?: number;
  onlyView?: boolean;
  overConfirm?: (data: any) => void;
  lineChangeable?: boolean;
}

const MusselAssessModal = ({
  visible,
  title,
  onCancel,
  onConfirm,
  data,
  lineData,
  updateID,
  onlyView,
  overConfirm,
  lineChangeable,
}: Props) => {
  const dispatch = useDispatch<any>();

  const farmsData = SelectFarmsByFarmID(lineData.farm_id);
  const customFields = SelectAssessFields(lineData.farm_id);
  const lang = useSelector(selectLang);
  const accSetting = useSelector(selectAccount);
  const unitType = accSetting?.display_unit;
  const manageWidth = !!accSetting?.manage_shellfish_width;
  const curBoat = useSelector(selectProfile)?.current_boat;
  const lineProps = getLineGrowingData(lineData);

  const getDensityUnitSpecie = (densityUnit: string | undefined) => {
    return densityUnit === 'kgs per m' ? 'kg_per_m' : 'spat_mer_m';
  };

  const [disabled, setDisabled] = useState(false);
  const [formData, setFormData] = useState<IFormData>({
    ...defaultForm,
    estimated_amount: lineProps.estimated_amount
      ? lineProps.estimated_amount / 1000
      : null,
    planned_date_harvest: toMillisecond(
      lineProps.planned_date_harvest ?? defaultForm.planned_date_harvest,
    ),
    custom_values: lineProps.assessment_custom_values ?? [],
    size_min: lineProps.shell_size_min ?? null,
    size_max: lineProps.shell_size_max ?? null,
    size_avg: lineProps.shell_size_avg ?? null,
    density: lineProps.density,
    density_max: lineProps.density_max,
    boat_id: data?.boat_id ?? curBoat?.id,
  });
  const [warning, setWaring] = useState(false);
  const [errors, setErrors] = useState<{
    min: string | null;
    max: string | null;
    avg: string | null;
  }>({
    min: null,
    max: null,
    avg: null,
  });
  const [curFarm, setCurFarm] = useState(
    farmsData.find(x => x.id === lineData.farm_id),
  );
  const [curLine, setCurLine] = useState(lineData);
  const [trackOrigin, setTrackOrigin] = useState(!!data?.origin_spat_id);
  const [assessBottom, setAssessBottom] = useState(!!data?.btm_size_avg);

  const handleLineChange = (v: string) => {
    if (!curFarm) return;
    const line = curFarm.lines.find(x => x.id === Number(v)) as
      | IMusselLineResource
      | undefined;
    if (!line?.growing_cycle) return;

    setCurLine(line);
    setFormData(prv => ({
      ...prv,
      planned_date_harvest: toMillisecond(
        line.growing_cycle?.main_seed.planned_date_harvest ?? 0,
      ),
    }));
  };
  const updateForm = (key: keyof IFormData, val: any) => {
    if (
      [
        'size_min',
        'size_max',
        'size_avg',
        'min_width',
        'max_width',
        'avg_width',
        'btm_size_min',
        'btm_size_max',
        'btm_size_avg',
        'estimated_amount',
      ].includes(key)
    ) {
      val = val.length <= 0 ? null : Number(val);
    }
    let tmp = { ...formData, [key]: val };
    if (key === 'size_min') {
      setErrors(p => ({ ...p, min: null, avg: null }));
      tmp.size_avg = Math.round(
        (Number(tmp.size_max) + Number(tmp.size_min)) / 2,
      );
    } else if (key === 'size_max') {
      setErrors(p => ({ ...p, max: null, avg: null }));
      tmp.size_avg = Math.round(
        (Number(tmp.size_max) + Number(tmp.size_min)) / 2,
      );
    } else if (key === 'min_width' || key === 'max_width') {
      tmp.avg_width = Math.round(
        (Number(tmp.max_width) + Number(tmp.min_width)) / 2,
      );
    } else if (key === 'btm_size_min' || key === 'btm_size_max') {
      tmp.btm_size_avg = Math.round(
        (Number(tmp.btm_size_max) + Number(tmp.btm_size_min)) / 2,
      );
    }
    setFormData(tmp);
  };
  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        type: 'error',
        message: translate(lang, message),
      }),
    );

  const validForm = () => {
    let res: any = true;
    let tmpErr = { ...errors };
    if (!formData.size_min) {
      tmpErr.min = translate(lang, 'Required field');
      res = false;
    }
    if (!formData.size_max) {
      tmpErr.max = translate(lang, 'Required field');
      res = false;
    }
    if (!formData.size_avg) {
      tmpErr.avg = translate(lang, 'Required field');
      res = false;
    }
    if (
      formData.density_max &&
      (!formData.density || formData.density_max <= formData.density!)
    ) {
      showError('Density max should be greater than density');
      res = false;
    }
    if (Number(formData.size_min) > Number(formData.size_max)) {
      tmpErr.avg = translate(lang, '_max_value_equal_greater');
      res = false;
    }
    if (
      formData.time_tracks &&
      toSecTimeTracks(formData.time_tracks) === false
    ) {
      showError('_invalid_time_range');
      res = false;
    }
    setErrors(tmpErr);
    if (!res) return null;

    res = {
      ...formData,
      assessment_date: toSecond(formData.assessment_date),
      planned_date_harvest: toSecond(formData.planned_date_harvest),
      seeding_id: formData?.seeding_id ?? curLine.growing_cycle?.main_seed.id,
      estimated_amount: formData.estimated_amount
        ? formData.estimated_amount * 1000
        : formData.estimated_amount,
      line_id: curLine.id,
    };
    if (assessBottom) {
      if (
        !formData.btm_size_avg ||
        !formData.btm_size_max ||
        !formData.btm_size_min
      ) {
        showError('_bottom_size_measuring_empty');
        return null;
      }
      if (formData.btm_size_max < formData.btm_size_min) {
        showError('_bottom_size_measuring_range');
        return null;
      }
      res.btm_size_min = formData.btm_size_min;
      res.btm_size_max = formData.btm_size_max;
      res.btm_size_avg = formData.btm_size_avg;
    } else {
      res.btm_size_min = res.btm_size_max = res.btm_size_avg = undefined;
    }

    if (res.time_tracks && res.time_tracks.length > 0) {
      const tt = toSecTimeTracks(res.time_tracks);
      if (!tt) {
        showError('_invalid_time_tracks_info');
        return null;
      }
      res.time_tracks = tt;
    }
    const cvs = formData.custom_values?.filter(x => !!x.field_val) ?? [];
    if (cvs.length > 0) {
      res.custom_values = cvs;
    } else {
      res.custom_values = undefined;
    }
    return res;
  };
  const handleConfirmClick = async () => {
    if (onlyView) return;
    const form = validForm();
    if (!form) return;

    if (
      !warning &&
      !updateID &&
      curLine.growing_cycle?.last_assessment &&
      (curLine.growing_cycle.last_assessment.shell_size.min >= form.size_min ||
        curLine.growing_cycle.last_assessment.shell_size.max >= form.size_max)
    ) {
      setWaring(true);
      return;
    }
    setDisabled(true);
    if (overConfirm) {
      overConfirm(form);
    } else {
      const res = !updateID
        ? await sendSingleRequest(
            form,
            'POST',
            'api/farm/line/assessment/assessments',
            true,
          )
        : await sendSingleRequest(
            form,
            'PUT',
            `api/farm/line/assessment/assessments/${updateID}`,
            true,
          );
      if (res.status) {
        if (form.tagged_ids) {
          dispatch(loadTags());
        }
        dispatch(
          showFeedback({
            isMessage: true,
            type: 'success',
            message: translate(lang, res.data?.message ?? 'Success'),
          }),
        );
        dispatch(loadFarmsData());
        dispatch(loadQueueData());
        onConfirm(res.data);
      } else {
        showError(res.data?.message ?? 'Request failed');
        setDisabled(false);
      }
    }
  };

  useEffect(() => {
    if (data) {
      let form = { ...data };
      if (form.assessment_date)
        form.assessment_date = toMillisecond(form.assessment_date);
      if (form.planned_date_harvest)
        form.planned_date_harvest = toMillisecond(form.planned_date_harvest);
      form.ready_to_harvest = !!data.ready_harvest || !!data.ready_to_harvest;
      if (form.time_tracks && form.time_tracks.length > 0) {
        form.time_tracks = data.time_tracks.map((x: any) => ({
          start_time: toMillisecond(x.start_time),
          finish_time: toMillisecond(x.finish_time),
        }));
      }
      if (data.image_pdf_files && data.image_pdf_files.length > 0) {
        let tmp: any = [];
        for (let x of data.image_pdf_files) {
          if (typeof x === 'object' && x !== null) {
            tmp.push(x);
          } else {
            tmp.push(parseImageUrlToFileItem(x));
          }
        }
        if (tmp && tmp.length > 0) {
          form.image_pdf_files = tmp;
        }
      }
      if (data.pdf_files && data.pdf_files.length > 0) {
        let tmp: any = [];
        for (let x of data.pdf_files) {
          if (typeof x === 'object' && x !== null) {
            tmp.push(x);
          } else {
            tmp.push(parseImageUrlToFileItem(x));
          }
        }
        if (tmp && tmp.length > 0) {
          form.pdf_files = tmp;
        }
      }
      if (form.estimated_amount) {
        form.estimated_amount = roundNumber(form.estimated_amount / 1000, 3);
      }
      setFormData(prv => ({ ...prv, ...form }));
    }
  }, [data]);

  const farmOptions = farmsData.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: `${x.name} - ${x.farm_number}`,
  }));
  const lineOptions = curFarm?.lines
    .filter(x => x.crop_type === 'MUSSEL' && !!x.growing_cycle)
    .map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: x.line_name,
    }));
  const densityUnit = curLine.growing_cycle?.main_seed.density_unit;

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={650}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, title ?? 'Mussel Assessment')}
          </Subtitle>
        </div>
        <div className='assessment-modal'>
          {lineChangeable && (
            <div className='mb-17 d-flex'>
              <div className='w-100 mr-7'>
                <Dropdown
                  label={translate(lang, 'Select farm')}
                  options={farmOptions}
                  value={curFarm?.id.toString()}
                  onChange={v =>
                    setCurFarm(farmsData.find(x => x.id === Number(v)))
                  }
                  disabled={onlyView}
                />
              </div>
              <div className='w-100 ml-7'>
                <Dropdown
                  label={translate(lang, 'Select line')}
                  options={lineOptions ?? []}
                  value={
                    !!lineOptions &&
                    lineOptions.some(x => Number(x.id) === curLine.id)
                      ? curLine.id.toString()
                      : curLine.line_name
                  }
                  onChange={handleLineChange}
                  disabled={onlyView}
                />
              </div>
            </div>
          )}
          {warning ? (
            <div className='mt-32 mb-64 pb-32 pt-17 pl-17 pr-17'>
              <Paragrapgh
                size={1}
                fontWeight={400}
                color='black-2'
                align='default'
              >
                {translate(lang, '_assessment_warning')}
              </Paragrapgh>
            </div>
          ) : (
            <div className='assessment-modal'>
              <div className='assessment-modal__condition d-flex justify-between'>
                <div className={`mxw-180 ${errors.min ? 'invalid-form' : ''}`}>
                  <Input
                    type='number'
                    label={`${getSizeMeasureLabel(lang, 'min', 'length', assessBottom ? 'Top' : undefined, manageWidth)} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                    value={numToStr(formData.size_min)}
                    onChange={e => updateForm('size_min', e.target.value)}
                    required={true}
                    disabled={onlyView}
                  />
                  {errors.min && (
                    <div className='invalid-feedback'>{errors.min}</div>
                  )}
                </div>
                <div className={`mxw-180 ${errors.max ? 'invalid-form' : ''}`}>
                  <Input
                    type='number'
                    label={`${getSizeMeasureLabel(lang, 'max', 'length', assessBottom ? 'Top' : undefined, manageWidth)} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                    value={numToStr(formData.size_max)}
                    onChange={e => updateForm('size_max', e.target.value)}
                    required={true}
                    disabled={onlyView}
                  />
                  {errors.max && (
                    <div className='invalid-feedback'>{errors.max}</div>
                  )}
                </div>
                <div className={`mxw-180 ${errors.avg ? 'invalid-form' : ''}`}>
                  <Input
                    type='number'
                    label={`${getSizeMeasureLabel(lang, 'avg', 'length', assessBottom ? 'Top' : undefined, manageWidth)} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                    value={numToStr(formData.size_avg)}
                    onChange={e => updateForm('size_avg', e.target.value)}
                    required={true}
                    disabled={onlyView}
                  />
                  {errors.avg && (
                    <div className='invalid-feedback'>{errors.avg}</div>
                  )}
                </div>
              </div>
              {manageWidth && (
                <div className='assessment-modal__condition d-flex justify-between'>
                  <div className='mxw-180'>
                    <Input
                      type='number'
                      label={`${getSizeMeasureLabel(lang, 'min', 'width')} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                      value={numToStr(formData.min_width)}
                      onChange={e => updateForm('min_width', e.target.value)}
                      disabled={onlyView}
                    />
                  </div>
                  <div className='mxw-180'>
                    <Input
                      type='number'
                      label={`${getSizeMeasureLabel(lang, 'max', 'width')} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                      value={numToStr(formData.max_width)}
                      onChange={e => updateForm('max_width', e.target.value)}
                      disabled={onlyView}
                    />
                  </div>
                  <div className='mxw-180'>
                    <Input
                      type='number'
                      label={`${getSizeMeasureLabel(lang, 'avg', 'width')} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                      value={numToStr(formData.avg_width)}
                      onChange={e => updateForm('avg_width', e.target.value)}
                      disabled={onlyView}
                    />
                  </div>
                </div>
              )}
              <div className='mb-17'>
                <CheckboxButton
                  label={translate(lang, '_measure_top_bottom_separately')}
                  checked={assessBottom}
                  onChange={e => setAssessBottom(e.target.checked)}
                />
              </div>
              {assessBottom && (
                <div className='assessment-modal__condition d-flex justify-between'>
                  <div className='mxw-180'>
                    <Input
                      type='number'
                      label={`${getSizeMeasureLabel(lang, 'min', 'length', 'Bottom', manageWidth)} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                      value={numToStr(formData.btm_size_min)}
                      onChange={e => updateForm('btm_size_min', e.target.value)}
                      required={true}
                      disabled={onlyView}
                    />
                  </div>
                  <div className='mxw-180'>
                    <Input
                      type='number'
                      label={`${getSizeMeasureLabel(lang, 'max', 'length', 'Bottom', manageWidth)} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                      value={numToStr(formData.btm_size_max)}
                      onChange={e => updateForm('btm_size_max', e.target.value)}
                      required={true}
                      disabled={onlyView}
                    />
                  </div>
                  <div className='mxw-180'>
                    <Input
                      type='number'
                      label={`${getSizeMeasureLabel(lang, 'avg', 'length', 'Bottom', manageWidth)} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                      value={numToStr(formData.btm_size_avg)}
                      onChange={e => updateForm('btm_size_avg', e.target.value)}
                      required={true}
                      disabled={onlyView}
                    />
                  </div>
                </div>
              )}
              <Datepicker
                className='mb-17'
                defaultValue={formData.assessment_date}
                label={translate(lang, 'Assessment date')}
                onChange={e =>
                  updateForm(
                    'assessment_date',
                    e!.toDate().getTime() <
                      Number(curLine.growing_cycle?.main_seed.planned_date_seed)
                      ? moment().toDate().getTime()
                      : e!.toDate().getTime(),
                  )
                }
                required={true}
                disabled={onlyView}
              />
              <Datepicker
                className='mb-17'
                defaultValue={formData.planned_date_harvest}
                label={translate(lang, 'Planned harvest date')}
                onChange={e =>
                  updateForm(
                    'planned_date_harvest',
                    e!.toDate().getTime() <
                      Number(curLine.growing_cycle?.main_seed.planned_date_seed)
                      ? moment().toDate().getTime()
                      : e!.toDate().getTime(),
                  )
                }
                required={true}
                disabled={onlyView}
              />
              <MinMaxInputForm
                label={translate(lang, 'Density')}
                min={formData.density}
                max={formData.density_max}
                unit={getUnitOfSpecie(
                  unitType,
                  getDensityUnitSpecie(densityUnit),
                )}
                onMinChange={v => updateForm('density', v)}
                onMaxChange={v => updateForm('density_max', v)}
              />
              {curLine.growing_cycle && (
                <BatchAssessView
                  status={trackOrigin}
                  lineData={curLine}
                  onChange={setTrackOrigin}
                  form={formData}
                  onBatchSelect={v =>
                    setFormData(prv =>
                      v
                        ? { ...prv, ...v }
                        : { ...prv, origin_spat_id: null, batch_seed_id: null },
                    )
                  }
                />
              )}
              <CheckboxButton
                className='mb-17'
                label={translate(lang, 'Ready to harvest')}
                checked={formData.ready_to_harvest}
                onChange={e => updateForm('ready_to_harvest', e.target.checked)}
                disabled={onlyView}
              />
              <div className='mb-17'>
                <Input
                  label={translate(lang, '_estimated_harvest_tonnes')}
                  type='number'
                  value={numToStr(formData.estimated_amount)}
                  onChange={e => updateForm('estimated_amount', e.target.value)}
                  disabled={onlyView}
                />
              </div>
              <CustomValuesForm
                values={formData.custom_values ?? []}
                fields={customFields}
                onChange={d =>
                  setFormData(prv => ({ ...prv, custom_values: d }))
                }
                disabled={onlyView}
              />
              <DirectionForm
                value={formData.direction}
                onChange={v => updateForm('direction', v)}
              />
              {formData.time_tracks && (
                <TimeTrackInput
                  data={formData.time_tracks}
                  onChange={t => updateForm('time_tracks', t)}
                  disabled={onlyView}
                  baseDate={formData.assessment_date}
                />
              )}
              <Input
                label={translate(lang, 'Comment')}
                placeholder={`${translate(lang, 'Type your comment')}...`}
                className='mb-17'
                type='textarea'
                value={formData.comment ?? ''}
                dataType='comment'
                onChange={e => updateForm('comment', e.target.value)}
                isOptional={true}
                disabled={onlyView}
              />
              <SelectBoat
                value={formData.boat_id}
                onChange={v => updateForm('boat_id', v)}
                className='mb-17'
              />
              <FilesWall
                label={translate(lang, 'PDF/Images')}
                fileList={formData.image_pdf_files ?? []}
                onChange={f => updateForm('image_pdf_files', f)}
              />
            </div>
          )}
        </div>
        {!updateID && (
          <TaggingManageForm
            className='mb-17 mt-17'
            lineData={curLine}
            tagIDs={formData.tagged_ids}
            onChange={ids => updateForm('tagged_ids', ids)}
          />
        )}
      </div>
      <div className='modal-button d-flex justify-end align-center'>
        {!warning ? (
          <Button
            width={'small'}
            size={2}
            type='fill'
            color='green'
            className='rsp-btn ml-16'
            onClick={handleConfirmClick}
            disabled={disabled || onlyView}
          >
            {translate(lang, 'Confirm')}
          </Button>
        ) : (
          <>
            <Button
              className='rsp-btn'
              width='small'
              size={2}
              type='fill'
              color='blue'
              onClick={() => setWaring(false)}
              disabled={disabled}
            >
              {translate(lang, 'Let me correct')}
            </Button>
            <Button
              width='small'
              size={2}
              type='fill'
              color='green'
              className='rsp-btn ml-16'
              onClick={handleConfirmClick}
              disabled={disabled}
            >
              {translate(lang, "Yes, it's valid")}
            </Button>
          </>
        )}
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default MusselAssessModal;
